import React, { useState } from 'react';
import { 
    Search, 
    RefreshCw, 
    CheckCircle, 
    XCircle, 
    Clock, 
    AlertCircle,
    Eye,
    Activity,
    Zap
} from 'lucide-react';
import axiosInstance from '../api/axiosInstance';

const PaymentStatusChecker = () => {
    const [orderId, setOrderId] = useState('');
    const [statusResult, setStatusResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [bulkCheckResult, setBulkCheckResult] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);

    // Check individual payment status
    const handleCheckStatus = async () => {
        if (!orderId.trim()) {
            setError('Please enter a valid Order ID');
            return;
        }

        setLoading(true);
        setError('');
        setStatusResult(null);

        try {
            const response = await axiosInstance.get(`/api/payment-status/check-status/${orderId.trim()}`);
            setStatusResult(response.data);
        } catch (err) {
            console.error('Payment status check error:', err);
            setError(err.response?.data?.error || 'Failed to check payment status');
        } finally {
            setLoading(false);
        }
    };

    // Check all pending payments
    const handleBulkCheck = async () => {
        setBulkLoading(true);
        setBulkCheckResult(null);

        try {
            const response = await axiosInstance.post('/api/payment-status/check-all-pending');
            setBulkCheckResult(response.data);
        } catch (err) {
            console.error('Bulk payment check error:', err);
            setBulkCheckResult({
                error: err.response?.data?.error || 'Failed to check pending payments'
            });
        } finally {
            setBulkLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'failed':
                return <XCircle className="w-5 h-5 text-red-500" />;
            case 'pending':
                return <Clock className="w-5 h-5 text-yellow-500" />;
            default:
                return <AlertCircle className="w-5 h-5 text-gray-500" />;
        }
    };

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
        switch (status) {
            case 'success':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'failed':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'pending':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                        <Activity className="w-6 h-6 mr-3 text-blue-600" />
                        Payment Status Checker
                    </h1>
                    <p className="text-gray-600">Check and update payment statuses using Razorpay APIs</p>
                </div>

                {/* Individual Payment Check */}
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Search className="w-5 h-5 mr-2 text-purple-600" />
                        Check Individual Payment Status
                    </h2>
                    
                    <div className="flex gap-3 mb-4">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={orderId}
                                onChange={(e) => setOrderId(e.target.value)}
                                placeholder="Enter Razorpay Order ID (e.g., order_xyz123)"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                disabled={loading}
                            />
                        </div>
                        <button
                            onClick={handleCheckStatus}
                            disabled={loading || !orderId.trim()}
                            className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                        >
                            {loading ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Eye className="w-4 h-4 mr-2" />
                            )}
                            {loading ? 'Checking...' : 'Check Status'}
                        </button>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center">
                                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                                <p className="text-red-700">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Status Result */}
                    {statusResult && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="font-medium text-gray-900 mb-3">Payment Status Result</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <p className="text-sm text-gray-500">Order ID</p>
                                    <p className="font-mono text-sm text-gray-900 break-all">{statusResult.orderId}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Database Status → Actual Status</p>
                                    <div className="flex items-center space-x-2">
                                        <span className={getStatusBadge(statusResult.databaseStatus)}>
                                            {statusResult.databaseStatus}
                                        </span>
                                        <span>→</span>
                                        <span className={getStatusBadge(statusResult.actualStatus)}>
                                            {statusResult.actualStatus}
                                        </span>
                                        {statusResult.databaseUpdated && (
                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                UPDATED
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg p-4 mb-4">
                                <p className="text-sm text-gray-600 mb-2">Status Reason:</p>
                                <p className="text-gray-900">{statusResult.statusReason}</p>
                            </div>

                            {/* Order Details */}
                            {statusResult.orderDetails && (
                                <div className="bg-white rounded-lg p-4 mb-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Order Details</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Amount</p>
                                            <p className="font-medium">{formatCurrency(statusResult.orderDetails.amount)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Currency</p>
                                            <p className="font-medium">{statusResult.orderDetails.currency}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Created At</p>
                                            <p className="font-medium">{formatDate(statusResult.orderDetails.created_at)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Attempts</p>
                                            <p className="font-medium">{statusResult.orderDetails.attempts}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Payment Details */}
                            {statusResult.paymentDetails && (
                                <div className="bg-white rounded-lg p-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Payment Details</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Payment ID</p>
                                            <p className="font-mono text-xs">{statusResult.paymentDetails.id}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Status</p>
                                            <span className={getStatusBadge(statusResult.paymentDetails.status)}>
                                                {statusResult.paymentDetails.status}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Method</p>
                                            <p className="font-medium">{statusResult.paymentDetails.method || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Amount</p>
                                            <p className="font-medium">{formatCurrency(statusResult.paymentDetails.amount)}</p>
                                        </div>
                                        {statusResult.paymentDetails.error_description && (
                                            <div className="col-span-2">
                                                <p className="text-gray-500">Error Description</p>
                                                <p className="text-red-600 text-sm">{statusResult.paymentDetails.error_description}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Bulk Check */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Zap className="w-5 h-5 mr-2 text-orange-600" />
                        Check All Pending Payments
                    </h2>
                    
                    <p className="text-gray-600 mb-4">
                        Check and update all pending payments from the last 24 hours using Razorpay APIs.
                    </p>

                    <button
                        onClick={handleBulkCheck}
                        disabled={bulkLoading}
                        className="px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                    >
                        {bulkLoading ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Zap className="w-4 h-4 mr-2" />
                        )}
                        {bulkLoading ? 'Checking All Payments...' : 'Check All Pending Payments'}
                    </button>

                    {/* Bulk Check Results */}
                    {bulkCheckResult && (
                        <div className="mt-6">
                            {bulkCheckResult.error ? (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-center">
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                                        <p className="text-red-700">{bulkCheckResult.error}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="grid grid-cols-3 gap-4 mb-4">
                                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                                            <p className="text-2xl font-bold text-blue-600">{bulkCheckResult.totalChecked}</p>
                                            <p className="text-sm text-blue-800">Total Checked</p>
                                        </div>
                                        <div className="text-center p-4 bg-green-50 rounded-lg">
                                            <p className="text-2xl font-bold text-green-600">{bulkCheckResult.updated}</p>
                                            <p className="text-sm text-green-800">Updated</p>
                                        </div>
                                        <div className="text-center p-4 bg-gray-100 rounded-lg">
                                            <p className="text-2xl font-bold text-gray-600">{bulkCheckResult.totalChecked - bulkCheckResult.updated}</p>
                                            <p className="text-sm text-gray-800">No Change</p>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-lg p-4">
                                        <p className="font-medium text-gray-900 mb-2">{bulkCheckResult.message}</p>
                                        
                                        {bulkCheckResult.results && bulkCheckResult.results.length > 0 && (
                                            <div className="space-y-2 mt-4">
                                                <h4 className="font-medium text-gray-900">Payment Updates:</h4>
                                                {bulkCheckResult.results.map((result, index) => (
                                                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                                        <div>
                                                            <p className="font-mono text-xs text-gray-600">{result.orderId}</p>
                                                            <p className="text-sm text-gray-900">{result.statusReason}</p>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <span className={getStatusBadge(result.previousStatus)}>
                                                                {result.previousStatus}
                                                            </span>
                                                            {result.updated && (
                                                                <>
                                                                    <span>→</span>
                                                                    <span className={getStatusBadge(result.currentStatus)}>
                                                                        {result.currentStatus}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentStatusChecker;
